import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, SITE_PHONE, getBreadcrumbItems } from '../../utils/seo';

/**
 * Product JSON-LD structured data
 */
export const ProductJsonLd = ({ product }) => {
  if (!product || !product.name) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description
      ? product.description.replace(/<[^>]+>/g, '').substring(0, 300)
      : product.name,
    image:
      product.images && product.images.length > 0
        ? product.images.map((img) => img.url || img)
        : [],
    sku: product._id || product.id,
    brand: {
      '@type': 'Brand',
      name: product.ShopName || SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/products/${product._id || product.id}`,
      priceCurrency: 'TND',
      price: product.price ? (product.price / 100).toFixed(2) : '0',
      availability:
        product.stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
    category: product.category || 'Furniture',
  };

  // Add aggregate rating if available
  if (product.rating && product.numberOfReviews > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.numberOfReviews,
      bestRating: 5,
      worstRating: 1,
    };
  }

  // Add individual reviews if available
  if (product.reviews && product.reviews.length > 0) {
    schema.review = product.reviews.slice(0, 5).map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.name || 'Customer',
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: 5,
      },
      reviewBody: review.comment || '',
    }));
  }

  return (
    <Helmet>
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/**
 * Breadcrumb JSON-LD structured data
 */
export const BreadcrumbJsonLd = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: getBreadcrumbItems(items),
  };

  return (
    <Helmet>
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/**
 * FAQ Page JSON-LD structured data
 */
export const FAQJsonLd = ({ questions }) => {
  if (!questions || questions.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/**
 * ItemList JSON-LD for product listing pages
 */
export const ProductListJsonLd = ({ products, listName = 'Products' }) => {
  if (!products || products.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: products.length,
    itemListElement: products.slice(0, 20).map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/products/${product._id || product.id}`,
      name: product.name,
    })),
  };

  return (
    <Helmet>
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
